import { LinearProgress, Stack, TextField } from "@mui/material";
import { useRef, type FC } from "react";
import { config } from "../../../../config";
import { type UUID } from "../../../../utils/uuid";
import { type EmailChannelName } from "../../../channels/types";
import { EmailAddressSelect } from "../../../email-moment/components/email-address-selector";
import { AutoCompleteSelector } from "../../../generic/components/auto-complete/auto-complete";
import { EmailTemplateLayout } from "../../../lexical-email-moment/email-template-layout";
import { useConstructedDesign } from "../../../lexical-email-moment/hooks/use-constructed-design";
import {
  useEmailToolbarSettings,
  type ToolbarSettings,
} from "../../../lexical-email-moment/hooks/use-email-toolbar-settings";
import { useSelectedEmailDesign } from "../../../lexical-email-moment/hooks/use-selected-email-design";
import { useFilterTags } from "../../../merge-tag/components/use-filter-tags";
import type { Tags } from "../../../merge-tag/types";
import { DefaultBodyPlugins } from "../../../message/editor/default-body-plugins";
import { DefaultContentEditable } from "../../../message/editor/default-content-editable";
import { useLexicalContext } from "../../../message/editor/lexical-context";
import { LexicalEditorWrapper } from "../../../message/editor/lexical-editor-wrapper";
import { GenericToolbar } from "../../../message/editor/plugins/generic-toolbar/generic-toolbar";
import type { SerialisedLexicalEditorDesign } from "../../../message/editor/types/serialised-editor-design";
import { EDITOR_NODES } from "../../../message/editor/utils/nodes";
import { tableNodeReplacement } from "../../../message/editor/utils/use-styleable-columns";
import type { ToTarget } from "../../../to/use-to-target";
import { useHandleFollowUpLexicalError } from "../../hooks/use-handle-follow-up-lexical-error";
import type { FollowUpChannelWithData } from "../../reducer/schema";

export interface FollowUpMessageEmailProps {
  moment_id: UUID;
  channel: Extract<FollowUpChannelWithData, { channel: "work_email" | "personal_email" }>;
  setChannelData: (data: FollowUpChannelWithData) => void;
  mergeTags: Tags;
  onChange: (val: SerialisedLexicalEditorDesign) => void;
  toTarget: ToTarget;
  design: SerialisedLexicalEditorDesign | null;
  restrictedEmailFrom: UUID[];
  errors: {
    design?: string;
    email_sender?: string;
    email_subject?: string;
  };
  disabled: boolean;
}

export const FollowUpMessageEmail: FC<FollowUpMessageEmailProps> = ({
  moment_id,
  channel,
  setChannelData,
  mergeTags,
  onChange,
  toTarget,
  design,
  restrictedEmailFrom,
  errors,
  disabled,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const { email_design_id } = channel.channelData;

  const selectedEmailDesign = useSelectedEmailDesign(email_design_id as UUID);

  const {
    constructDesign: { data: constructedDesign, loading },
  } = useConstructedDesign(design, selectedEmailDesign);

  const toolbarSettings = useEmailToolbarSettings({
    selectedEmailDesign,
    templateSettings: design?.template,
    setEmailDesign: (x) =>
      setChannelData({
        ...channel,
        channelData: {
          ...channel.channelData,
          email_design_id: x?.id ?? null,
        },
      }),
    blacklistedElements: ["dynamicContent", "pollButton"],
  });

  const onError = useHandleFollowUpLexicalError(moment_id);

  const filterTags = useFilterTags(
    mergeTags.filter((x) => x.version === 2),
    toTarget,
  );

  if (loading || !constructedDesign || !selectedEmailDesign || !toolbarSettings)
    return <LinearProgress />;

  return (
    <Stack direction="column" spacing={2}>
      <EmailAddressSelect
        value={channel.channelData.email_sender || config.defaultEmailFrom}
        onChange={(email_sender) =>
          setChannelData({
            ...channel,
            channelData: {
              ...channel.channelData,
              email_sender,
            },
          })
        }
        disabled={disabled}
        restrictedEmailFrom={restrictedEmailFrom}
        fullWidth
        size="small"
        error={!!errors.email_sender}
        helperText={errors.email_sender}
      />
      <AutoCompleteSelector
        defaultValue={channel.channelData.email_subject || ""}
        onChange={(email_subject) =>
          setChannelData({
            ...channel,
            channelData: {
              ...channel.channelData,
              email_subject,
            },
          })
        }
        filterTags={filterTags}
        renderInput={(params) => (
          <TextField
            {...params}
            disabled={disabled}
            variant="outlined"
            label="Subject"
            placeholder="Enter your email subject"
            helperText={errors.email_subject ?? "Type {{ to insert a merge tag into your subject"}
            fullWidth
            size="small"
            error={!!errors.email_subject}
          />
        )}
      />
      <LexicalEditorWrapper
        channelName={channel.channel}
        keyID={email_design_id?.toString()}
        onError={(...args) => void onError(...args).catch(console.error)}
        disabled={disabled}
        error={errors.design}
        renderLayout={(editorComponent) => (
          <EmailTemplateLayout
            settings={constructedDesign.settings}
            header={constructedDesign.header ?? undefined}
            headerSettings={constructedDesign.headerSettings ?? undefined}
            footer={constructedDesign.footer ?? undefined}
            editorComponent={editorComponent}
            templateSettings={design?.template}
            emailEditorScrollRef={ref}
          />
        )}
        filterTags={filterTags}
        format="email"
        design={constructedDesign?.design ?? undefined}
        nodes={tableNodeReplacement(EDITOR_NODES["email"])}
        slots={{
          contentEditable: ({ focusEditor, onRef }) => (
            <DefaultContentEditable
              focusEditor={focusEditor}
              onRef={onRef}
              channel={channel.channel}
              disabled={disabled}
            />
          ),
          plugins: {
            head: () => (
              <EmailHeadPlugins
                channel={channel.channel}
                toolbarSettings={toolbarSettings}
                disabled={disabled}
              />
            ),
            body: (historyState) => (
              <DefaultBodyPlugins
                auto_focus={false}
                channel={channel.channel}
                format="email"
                disabled={disabled}
                onChange={(val) => onChange(val.toJSON())}
                editorScrollRef={ref}
                toolbarSettings={toolbarSettings}
                styleableColumnConfig={constructedDesign.settings.layout}
                historyState={historyState}
                v1Tags={mergeTags.filter((x) => x.version === 1)}
              />
            ),
          },
        }}
      />
    </Stack>
  );
};

const EmailHeadPlugins: FC<{
  channel: EmailChannelName;
  toolbarSettings: ToolbarSettings;
  disabled?: boolean;
}> = ({ toolbarSettings, disabled }) => {
  // This will eventually be rendered by the LexicalEditor calling the slot, so we can
  // use the context here
  const {
    editorModal: { handleOpen },
  } = useLexicalContext();

  return (
    <GenericToolbar
      channel={toolbarSettings.channel}
      topLevelElements={toolbarSettings.topLevelElements}
      disabled={!!disabled}
      openModal={handleOpen}
      stickyTop={0}
    />
  );
};
